import { Controller } from "@hotwired/stimulus"
import { chime } from "../lair/chime.js"

// The bout page. The server is the referee: one POST per move, and the
// answer is the lines that happened and the numbers after them. This
// controller's whole job is to say those lines one at a time, at a reading
// pace, with the figure that acted lunging and the one it hit flinching, and
// then to move the vigour bars to where the server says they are. Nothing
// here decides a fight.

// Between lines. Long enough to read one, short enough that four do not drag.
const LINE_MS = 720
// Heals (Settle, Thorn lash) a side may have in one bout; the server's Combat::Sim::HEAL_LIMIT.
const HEAL_LIMIT = 3
// A line whose move is drawn across the sand (a breath, a lunge that lands)
// is given a little longer, so the hit is seen before the next line starts.
const MOVE_MS = 980
const FLINCH_MS = 420
const SVG = "http://www.w3.org/2000/svg"

// Where each figure's mouth, chest and feet are in the scene's 800x400 box
// if the drawing cannot be measured. The figures are drawn 300 wide from
// x=50 and x=450, facing each other; measured, a small foe's chest is where
// the small foe is (anatomy()).
const ANATOMY = {
  me: { mouth: { x: 300, y: 165 }, chest: { x: 200, y: 225 }, feet: { x: 200, y: 345 } },
  foe: { mouth: { x: 500, y: 165 }, chest: { x: 600, y: 225 }, feet: { x: 600, y: 345 } },
  // A friend's dragon (Allies), 230 wide from x=10, a pace behind mine.
  ally: { mouth: { x: 208, y: 188 }, chest: { x: 125, y: 236 }, feet: { x: 125, y: 333 } }
}

// The breaths' colours in the ring; brawn has none and hits with a spark.
const TINT = {
  ember: "#FF9A3C", frost: "#BFE9FF", storm: "#D9C8FF", verdant: "#7BE28A", shadow: "#6B4C8A", brawn: "#F5E6A8"
}

// Motes per breath, and how they fly: fire billows, frost drifts, storm
// cracks, verdant scatters, shadow creeps.
const BREATH_DRAW = {
  ember: { count: 26, size: [ 9, 22 ], spread: 70, ms: 560 },
  frost: { count: 22, size: [ 6, 14 ], spread: 64, ms: 660 },
  storm: { count: 16, size: [ 5, 10 ], spread: 36, ms: 340 },
  verdant: { count: 22, size: [ 8, 16 ], spread: 80, ms: 600 },
  shadow: { count: 22, size: [ 12, 26 ], spread: 56, ms: 760 }
}

export default class extends Controller {
  static targets = [ "root", "mine", "foe", "mineFill", "foeFill", "mineHp", "foeHp", "moves", "outcome", "note", "log", "turn", "heading", "fx", "mineConditions", "foeConditions",
                     "ally", "allyFill", "allyHp", "allyConditions" ]
  static values = {
    id: String,
    turn: { type: Number, default: 0 },
    over: Boolean,
    mineName: String,
    foeName: String,
    allyName: String,
    hp: Object,
    hpMax: Object,
    roseFrom: { type: Number, default: -1 },
    back: String
  }

  connect() {
    this.busy = false
    this.gone = false
    this.reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
    this.logTarget.scrollTop = this.logTarget.scrollHeight
    this.rise()
    // A phone brings a page back from its memory as it was left — the moves
    // lit, a narration half said — and not as the fight stands. Restored so,
    // the page asks the server again rather than trusting itself.
    this.onShow = (event) => { if (event.persisted) location.reload() }
    window.addEventListener("pageshow", this.onShow)
  }

  // A potion drunk on this page (RemediesController sends the bar's old
  // reading back as ?from=): the page drew the bar where it was, and here it
  // rises to where the dragon stands now, so the drink is seen and not only
  // read about.
  rise() {
    const from = this.roseFromValue
    const to = this.hpValue?.me
    if (!this.overValue || from < 0 || typeof to !== "number" || to <= from) return

    const go = () => {
      this.setBar(this.mineFillTarget, this.mineHpTarget, to, this.hpMaxValue.me)
      this.mineFillTarget.closest(".ll-bout__card")?.classList.add("ll-bout__card--risen")
      chime("care")
    }
    if (this.reduced) go()
    else setTimeout(go, 260)
  }

  // A narration in progress stops at the next line rather than going on
  // appending to a log the page has already left behind.
  disconnect() {
    this.gone = true
    window.removeEventListener("pageshow", this.onShow)
  }

  async play(event) {
    const move = event.params.move
    if (!move || this.busy || this.overValue) return

    this.busy = true
    this.setMovesEnabled(false)

    try {
      // The turn this page is playing goes with the move: the action's own
      // number. A page behind the fight is told so (409) and catches up.
      const response = await fetch(`/bouts/${this.idValue}/turn`, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({ move, turn: this.turnValue })
      })
      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        this.say(payload.error || "That could not be done.")
        if (payload.stale) this.catchUp(payload)
        else if (payload.over) { this.settle(payload); this.finish(payload) }
        return
      }

      await this.narrate(payload)
      this.settle(payload)
      if (payload.over) this.finish(payload)
    } finally {
      this.busy = false
      if (!this.overValue) this.setMovesEnabled(true)
    }
  }

  // Each line in turn: whoever's name opens it acted; if the line took
  // vigour off somebody, that somebody flinches. The bars move after the
  // last line, once, to where the server left them.
  async narrate(payload) {
    const lines = payload.lines || []
    const beats = payload.beats || []
    for (let index = 0; index < lines.length; index++) {
      if (this.gone) return
      const line = lines[index]
      const beat = beats[index]
      this.appendLine(line, { last: payload.over && index === lines.length - 1 })
      const drawn = this.perform(line, beat)
      if (index < lines.length - 1) await this.pause(this.reduced ? 120 : drawn ? MOVE_MS : LINE_MS)
    }
    this.turnValue = payload.turn ?? payload.state?.turn ?? this.turnValue + 1
    this.turnTarget.textContent = `Turn ${this.turnValue + 1}`
  }

  // The fight moved on without this page: the log is redrawn from the
  // server's, the bars set, the turn taken from it, and if it is over, it
  // is over here too. No narration — these are lines already said.
  catchUp(payload) {
    if (Array.isArray(payload.log)) {
      this.logTarget.replaceChildren()
      payload.log.forEach((line, index) => this.appendLine(line, { last: payload.over && index === payload.log.length - 1 }))
    }
    this.turnValue = payload.turn ?? payload.state?.turn ?? this.turnValue
    this.turnTarget.textContent = `Turn ${this.turnValue + 1}`
    this.settle(payload)
    if (payload.over) this.finish(payload)
  }

  // Draws the line. With a beat (Combat::Sim::Result#beats) the move is drawn
  // as itself: a breath crosses the sand in its own colour and the other
  // side is lit by it; a brawn move lunges and strikes a spark; a miss is a
  // sidestep; a ward, a mend, a roar each have their own mark. Without one
  // (a page older than the server) the line is read for who acted and who
  // was hit, and it is a lunge and a flinch. Returns whether something was
  // drawn across the sand, so the narration gives it time.
  perform(line, beat) {
    if (this.reduced) return false
    if (!beat) return this.performFromLine(line)

    const actor = this.figureFor(beat.side)
    // Two against one (Allies), the beat names who was gone for.
    const otherSide = beat.target || (beat.side === "foe" ? "me" : "foe")
    const target = this.figureFor(otherSide)
    if (!actor) return false

    if (beat.kind === "yield") {
      actor.classList.add("ll-bout__figure--down")
      return false
    }
    if (beat.kind === "idea") {
      this.replay(actor, "ll-bout__figure--idea")
      return false
    }
    if (beat.kind !== "move") return false

    const breath = beat.type !== "brawn"
    const damaging = beat.power > 0
    const tint = TINT[beat.type] || TINT.brawn

    if (!beat.hit) {
      if (breath) this.breathe(beat.side, otherSide, beat.type, { missed: true })
      else this.replay(actor, "ll-bout__figure--strike")
      setTimeout(() => this.replay(target, "ll-bout__figure--dodge"), breath ? 260 : 140)
      return true
    }

    let landsAt = 0
    if (damaging && breath) {
      landsAt = this.breathe(beat.side, otherSide, beat.type)
    } else if (damaging) {
      this.replay(actor, "ll-bout__figure--strike")
      landsAt = 140
    } else {
      this.replay(actor, "ll-bout__figure--call", 520)
    }

    if (damaging) {
      setTimeout(() => {
        this.replay(target, beat.strong ? "ll-bout__figure--hit-hard" : "ll-bout__figure--hit")
        this.flash(target, tint)
        this.burst(this.anatomy(otherSide).chest, beat.type, beat.strong ? 1.5 : 1)
        chime(breath ? "splash" : "sit")
      }, landsAt)
    }

    // The move's second word, drawn after (or instead of) the blow.
    const effectAt = landsAt + (damaging ? 180 : 0)
    setTimeout(() => this.drawEffect(beat, actor, target, otherSide), effectAt)

    return damaging || breath
  }

  drawEffect(beat, actor, target, otherSide) {
    switch (beat.effect) {
      case "brace":
        this.ward(beat.side)
        break
      case "mend":
        this.motes(this.anatomy(beat.side).feet, this.anatomy(beat.side).mouth, "verdant", { count: 10, rise: true })
        break
      case "rattle":
        this.ripple(beat.side)
        setTimeout(() => this.replay(target, "ll-bout__figure--cower", 600), 120)
        break
      case "chill":
        this.motes({ x: this.anatomy(otherSide).chest.x, y: this.anatomy(otherSide).chest.y - 90 },
                   this.anatomy(otherSide).feet, "frost", { count: 10, drift: true })
        break
      case "swift":
        this.streaks(beat.side)
        break
      case "drain":
        if (beat.back > 0) this.motes(this.anatomy(otherSide).chest, this.anatomy(beat.side).chest, "verdant", { count: 8 })
        break
    }
  }

  // The older drawing, from the words alone.
  performFromLine(line) {
    const mine = line.startsWith(this.mineNameValue)
    const foe = line.startsWith(this.foeNameValue)
    const actor = mine ? this.mineTarget : foe ? this.foeTarget : null
    if (!actor) return false

    const hitMine = /\d+ off /.test(line) && line.includes(`off ${this.mineNameValue}`)
    const hitFoe = /\d+ off /.test(line) && line.includes(`off ${this.foeNameValue}`)
    const target = hitMine ? this.mineTarget : hitFoe ? this.foeTarget : null

    if (/ yields\.$/.test(line)) {
      actor.classList.add("ll-bout__figure--down")
      return false
    }

    this.replay(actor, "ll-bout__figure--strike")
    if (target && target !== actor) {
      setTimeout(() => {
        this.replay(target, "ll-bout__figure--hit")
        chime("sit")
      }, 140)
    }
    return false
  }

  figureFor(side) {
    if (side === "ally") return this.hasAllyTarget ? this.allyTarget : null
    return side === "me" ? this.mineTarget : side === "foe" ? this.foeTarget : null
  }

  // The figure's mouth, chest and feet, measured from what is actually drawn
  // (a rat's chest is a rat's height off the sand, not a dragon's). Measured
  // once per figure; the fallback is the box the figure is drawn in.
  anatomy(side) {
    this.measured ||= {}
    if (this.measured[side]) return this.measured[side]

    const fallback = ANATOMY[side]
    const use = this.figureFor(side)?.querySelector("use")
    let box = null
    try { box = use?.getBBox() } catch { box = null }
    if (!box || box.width < 10 || box.height < 10) return fallback

    const toward = side === "foe" ? -1 : 1
    const centre = box.x + box.width / 2
    this.measured[side] = {
      mouth: { x: centre + toward * box.width * 0.36, y: box.y + box.height * 0.34 },
      chest: { x: centre, y: box.y + box.height * 0.55 },
      feet: { x: centre, y: box.y + box.height * 0.97 }
    }
    return this.measured[side]
  }

  // ---------------------------------------------------------------- the fx

  // A breath from one mouth to the other's chest: a fan of motes in the
  // breath's colour, each on its own slightly different path, the breather
  // rearing as it goes. Returns when the first of them lands.
  breathe(from, to, type, { missed = false } = {}) {
    const draw = BREATH_DRAW[type] || BREATH_DRAW.ember
    const start = this.anatomy(from).mouth
    const end = missed
      ? { x: this.anatomy(to).chest.x + (to === "foe" ? 90 : -90), y: this.anatomy(to).chest.y - 70 }
      : this.anatomy(to).chest

    this.replay(this.figureFor(from), "ll-bout__figure--rear", 560)
    this.plume(start, end, type, draw.ms)
    if (type === "storm") this.bolt(start, end)

    for (let index = 0; index < draw.count; index++) {
      const t = index / draw.count
      const wobble = (Math.random() - 0.5) * draw.spread
      const size = draw.size[0] + Math.random() * (draw.size[1] - draw.size[0])
      this.mote(type, {
        x0: start.x + (Math.random() - 0.5) * 12, y0: start.y + (Math.random() - 0.5) * 12,
        x1: end.x + wobble, y1: end.y + (Math.random() - 0.5) * draw.spread,
        r: size, delay: t * 140, ms: draw.ms + Math.random() * 120,
        shape: type === "verdant" ? "leaf" : "round"
      })
    }
    return draw.ms * 0.8
  }

  // The body of the breath: a soft cone from the mouth that widens toward
  // where it lands, under the motes, so the breath reads as one thing and
  // not a scatter of dots.
  plume(start, end, type, ms) {
    const dx = end.x - start.x
    const dy = end.y - start.y
    const length = Math.hypot(dx, dy) || 1
    const nx = -dy / length
    const ny = dx / length
    const half = 58
    const d = `M ${start.x} ${start.y} ` +
      `Q ${start.x + dx * 0.5 + nx * half * 0.9} ${start.y + dy * 0.5 + ny * half * 0.9} ${end.x + nx * half} ${end.y + ny * half} ` +
      `L ${end.x - nx * half} ${end.y - ny * half} ` +
      `Q ${start.x + dx * 0.5 - nx * half * 0.9} ${start.y + dy * 0.5 - ny * half * 0.9} ${start.x} ${start.y} Z`
    const node = this.fxNode("path", `ll-fx ll-fx--plume ll-fx--${type}`, { d })
    node.style.setProperty("--ms", `${Math.round(ms + 200)}ms`)
    node.style.setProperty("--ox", `${start.x}px`)
    node.style.setProperty("--oy", `${start.y}px`)
    this.retire(node, ms + 200)
  }

  // The storm's bolt: a jagged line from mouth to chest that flashes and goes.
  bolt(start, end) {
    const steps = 6
    const points = [ `${start.x},${start.y}` ]
    for (let index = 1; index < steps; index++) {
      const t = index / steps
      points.push(`${start.x + (end.x - start.x) * t},${start.y + (end.y - start.y) * t + (Math.random() - 0.5) * 40}`)
    }
    points.push(`${end.x},${end.y}`)
    const line = this.fxNode("polyline", "ll-fx ll-fx--bolt", { points: points.join(" ") })
    this.retire(line, 360)
  }

  // A mote of breath: a circle (or a leaf) that flies from one point to
  // another and fades. Positions ride on CSS variables so one keyframe serves
  // every mote.
  mote(type, { x0, y0, x1, y1, r, delay = 0, ms = 500, shape = "round" }) {
    const node = shape === "leaf"
      ? this.fxNode("ellipse", `ll-fx ll-fx--mote ll-fx--${type}`, { rx: r, ry: r * 0.55, cx: 0, cy: 0 })
      : this.fxNode("circle", `ll-fx ll-fx--mote ll-fx--${type}`, { r, cx: 0, cy: 0 })
    node.style.setProperty("--x0", `${x0}px`)
    node.style.setProperty("--y0", `${y0}px`)
    node.style.setProperty("--x1", `${x1}px`)
    node.style.setProperty("--y1", `${y1}px`)
    node.style.setProperty("--delay", `${Math.round(delay)}ms`)
    node.style.setProperty("--ms", `${Math.round(ms)}ms`)
    this.retire(node, delay + ms + 60)
  }

  // A handful of motes from one place to another — a mend rising from the
  // feet, a chill settling from above, a drain drawn back across the sand.
  motes(start, end, type, { count = 8, rise = false, drift = false } = {}) {
    for (let index = 0; index < count; index++) {
      const spread = rise || drift ? 70 : 24
      this.mote(type, {
        x0: start.x + (Math.random() - 0.5) * spread, y0: start.y + (Math.random() - 0.5) * 20,
        x1: end.x + (Math.random() - 0.5) * spread, y1: end.y + (Math.random() - 0.5) * 20,
        r: 3 + Math.random() * 4, delay: index * (drift ? 60 : 35), ms: drift ? 900 : 640
      })
    }
  }

  // The blow landing: a ring of the breath's colour that blooms and goes.
  burst(at, type, scale = 1) {
    const node = this.fxNode("circle", `ll-fx ll-fx--burst ll-fx--${type}`, { cx: at.x, cy: at.y, r: 18 * scale })
    this.retire(node, 460)
  }

  // A brace: a ward drawn round the figure that holds a moment.
  ward(side) {
    const at = this.anatomy(side).chest
    const node = this.fxNode("ellipse", "ll-fx ll-fx--ward", { cx: at.x, cy: at.y + 20, rx: 120, ry: 140 })
    this.retire(node, 900)
  }

  // A roar: rings out from the mouth.
  ripple(side) {
    const at = this.anatomy(side).mouth
    for (let index = 0; index < 3; index++) {
      const node = this.fxNode("circle", "ll-fx ll-fx--ripple", { cx: at.x, cy: at.y, r: 12 })
      node.style.setProperty("--delay", `${index * 110}ms`)
      this.retire(node, 700 + index * 110)
    }
  }

  // Haste: streaks behind the figure, the way it moves.
  streaks(side) {
    const away = side === "foe" ? 1 : -1
    const at = this.anatomy(side).chest
    for (let index = 0; index < 4; index++) {
      const y = at.y - 40 + index * 28
      const x = at.x + away * (40 + Math.random() * 30)
      const node = this.fxNode("line", "ll-fx ll-fx--streak", { x1: x, y1: y, x2: x + away * 60, y2: y })
      node.style.setProperty("--delay", `${index * 50}ms`)
      this.retire(node, 500)
    }
  }

  // The struck figure lit in the breath's colour for a beat.
  flash(figure, tint) {
    figure.style.setProperty("--ll-tint", tint)
    this.replay(figure, "ll-bout__figure--lit", 520)
  }

  fxNode(tag, className, attributes) {
    if (!this.hasFxTarget) return document.createElementNS(SVG, tag)
    const node = document.createElementNS(SVG, tag)
    node.setAttribute("class", className)
    for (const [ key, value ] of Object.entries(attributes)) node.setAttribute(key, String(value))
    this.fxTarget.appendChild(node)
    return node
  }

  retire(node, afterMs) {
    setTimeout(() => node.remove(), afterMs + 80)
  }

  replay(node, className, ms = FLINCH_MS) {
    node.classList.remove(className)
    // Forces the animation to restart when the same class is re-applied.
    void node.getBoundingClientRect()
    node.classList.add(className)
    setTimeout(() => node.classList.remove(className), ms + 40)
  }

  settle(payload) {
    this.setHealsLeft(payload.state)
    this.setConditions(payload.conditions)
    if (!payload.hp) return

    this.hpValue = payload.hp
    this.setBar(this.mineFillTarget, this.mineHpTarget, payload.hp.me, payload.hp_max?.me ?? this.hpMaxValue.me)
    this.setBar(this.foeFillTarget, this.foeHpTarget, payload.hp.foe, payload.hp_max?.foe ?? this.hpMaxValue.foe)
    if (this.hasAllyFillTarget && typeof payload.hp.ally === "number") {
      this.setBar(this.allyFillTarget, this.allyHpTarget, payload.hp.ally, payload.hp_max?.ally ?? this.hpMaxValue.ally)
    }
  }

  setBar(fill, label, hp, max) {
    const fraction = Math.max(0, Math.min(1, hp / Math.max(max, 1)))
    fill.style.width = `${Math.round(fraction * 100)}%`
    fill.classList.toggle("ll-bout__vigour-fill--low", fraction <= 0.3)
    fill.closest("[role='meter']")?.setAttribute("aria-valuenow", String(hp))
    label.textContent = String(hp)
  }

  finish(payload) {
    this.overValue = true
    this.rootTarget.classList.add("ll-bout--over")
    if (payload.outcome_html) this.outcomeTarget.innerHTML = payload.outcome_html
    this.noteTarget.textContent = ""
    if (this.hasHeadingTarget) this.headingTarget.textContent = "How it ended"
    const turns = payload.turn ?? payload.state?.turn ?? this.turnValue
    this.turnTarget.textContent = `${turns} ${turns === 1 ? "turn" : "turns"}`
    // The side that yielded is down, however the page learned of it.
    if (payload.status === "won") this.foeTarget.classList.add("ll-bout__figure--down")
    if (payload.status === "lost") this.mineTarget.classList.add("ll-bout__figure--down")
    chime(payload.status === "won" ? "coin" : "care")
  }

  appendLine(line, { last = false } = {}) {
    const item = document.createElement("li")
    item.className = "ll-bout__line"
    if (line.startsWith(this.mineNameValue)) item.classList.add("ll-bout__line--mine")
    if (this.allyNameValue && line.startsWith(this.allyNameValue)) item.classList.add("ll-bout__line--ally")
    if (last) item.classList.add("ll-bout__line--end")
    item.textContent = line
    this.logTarget.appendChild(item)
    this.logTarget.scrollTop = this.logTarget.scrollHeight
  }

  say(message) {
    this.noteTarget.textContent = message
  }

  // Every button on, or every button off — except a Settle with no heals
  // left, which stays off: three a bout is the limit (Combat::Sim::HEAL_LIMIT).
  // A spent Thorn lash stays on; it still hits.
  setMovesEnabled(enabled) {
    this.movesTarget.querySelectorAll("button").forEach((button) => {
      const spent = button.dataset.boutHeal === "mend" && Number(button.dataset.healsLeft) <= 0
      button.disabled = !enabled || spent
    })
  }

  // What each side is under, as the server words it (Combat::Sim.conditions).
  setConditions(conditions) {
    if (!conditions) return
    const draw = (list, words) => {
      list.replaceChildren(...(words || []).map((text) => {
        const item = document.createElement("li")
        item.className = "ll-bout__condition"
        item.textContent = text
        return item
      }))
    }
    if (this.hasMineConditionsTarget) draw(this.mineConditionsTarget, conditions.me)
    if (this.hasFoeConditionsTarget) draw(this.foeConditionsTarget, conditions.foe)
    if (this.hasAllyConditionsTarget) draw(this.allyConditionsTarget, conditions.ally)
  }

  // The healing buttons' count, from the turn's state.
  setHealsLeft(state) {
    const me = state?.me
    if (!me || (typeof me.heals !== "number" && typeof me.mends !== "number")) return
    const left = Math.max(0, HEAL_LIMIT - (me.heals ?? 0) - (me.mends ?? 0))
    this.movesTarget.querySelectorAll("[data-bout-heal]").forEach((button) => {
      button.dataset.healsLeft = String(left)
      const count = button.querySelector("[data-bout-heal-count]")
      if (count) {
        count.textContent = String(left)
        count.nextSibling.textContent = ` ${left === 1 ? "heal" : "heals"} left`
      }
      button.classList.toggle("ll-move--spent", left === 0)
    })
  }

  pause(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
    }
  }
}
